import type { Color, Dir } from '@xox/shared'
import type { CSSProperties } from 'react'
import { SHIRT_FILL } from '../../lib/flair'
import type { Facing } from '../anim'

type SkaterMood = 'idle' | 'falling' | 'finished'

interface Props {
  /** Profile color → T-shirt. */
  color: Color
  facing?: Facing
  /** Direction currently sliding in; leans the body. */
  lean?: Dir | null
  mood?: SkaterMood
  /** Accessible name for standalone use (profile preview). */
  label?: string
  className?: string
}

const LEAN_CLASS: Record<Dir, string> = {
  right: 'rotate-6',
  left: '-rotate-6',
  up: 'scale-y-95',
  down: 'scale-y-105',
}

/**
 * The little person who slides around the ice. Authored facing right in a 28×36 box; parts are
 * kept to two or three flat colors each so it still reads in a 36px cell.
 */
export function Skater({
  color,
  facing = 'right',
  lean = null,
  mood = 'idle',
  label,
  className = '',
}: Props) {
  // Size the wrapper (height or width class from the caller); CSS aspect-ratio supplies the other
  // side. WebKit renders an SVG with `width: auto` and a percentage height as 0px wide, so the
  // <svg> itself never relies on its intrinsic ratio.
  // Inside the board (itself role="img") this label is presentational; standalone it is read out.
  return (
    <span
      className={`inline-block aspect-[7/9] shrink-0 transition-transform duration-150 ${lean ? LEAN_CLASS[lean] : ''} ${className}`}
      style={{ '--shirt': SHIRT_FILL[color] } as CSSProperties}
    >
      <svg viewBox="0 0 28 36" role="img" aria-label={label ?? 'Skater'} className="size-full">
        <ellipse cx="14" cy="34" rx="8" ry="1.6" fill="var(--skater-line)" opacity=".18" />
        <g transform={facing === 'left' ? 'translate(28 0) scale(-1 1)' : undefined}>
          {/* boots */}
          <rect x="7.5" y="29.5" width="6" height="4" rx="1.5" fill="var(--skater-boot)" />
          <rect x="14.5" y="29.5" width="6.5" height="4" rx="1.5" fill="var(--skater-boot)" />
          {/* trousers */}
          <path d="M8 21 H20 V30 H15 L14 25 L13 30 H8 Z" fill="var(--skater-pants)" />
          {/* shirt + sleeves */}
          <rect x="6" y="11.5" width="16" height="11" rx="3" fill="var(--shirt)" />
          <circle cx="5.5" cy="15" r="3" fill="var(--shirt)" />
          <circle cx="22.5" cy="15" r="3" fill="var(--shirt)" />
          <rect x="6" y="19" width="16" height="3.5" fill="var(--skater-line)" opacity=".15" />
          {/* hands */}
          <circle cx="4.5" cy="19.5" r="2" fill="var(--skater-skin)" />
          <circle cx="23.5" cy="19.5" r="2" fill="var(--skater-skin)" />
          {/* head + hair */}
          <circle cx="14" cy="7.5" r="6" fill="var(--skater-skin)" />
          <path
            d="M8 7.5 a6 6 0 0 1 12 0 v.8 h-1.6 l-1.4-2.2 -1.8 2.2 -1.8-2.2 -1.8 2.2 -1.4-2.2 H8 Z"
            fill="var(--skater-hair)"
          />
          {/* face, looking the way we travel */}
          <circle cx="13.2" cy="8.2" r=".95" fill="var(--skater-line)" />
          <circle cx="17.2" cy="8.2" r=".95" fill="var(--skater-line)" />
          {mood === 'falling' ? (
            <circle cx="15.6" cy="11" r="1.3" fill="var(--skater-line)" />
          ) : (
            <path
              d={mood === 'finished' ? 'M13 10.4 q2.6 2.4 5.2 0' : 'M13.6 10.8 q1.8 1.2 3.6 0'}
              fill="none"
              stroke="var(--skater-line)"
              strokeWidth=".8"
              strokeLinecap="round"
            />
          )}
        </g>
      </svg>
    </span>
  )
}
